import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import AdminModal from '../../components/Admin/AdminModal'
import { IconLabel } from '../../components/icons'

const AUDIENCE_LABELS = {
  all: 'All users',
  GP: 'GP only',
  LP: 'LP only',
  Startup: 'Startup only',
  ServiceProvider: 'Service Provider only',
}

const EMPTY_FORM = {
  title: '',
  body: '',
  audience: 'all',
  isActive: true,
}

function formatWhen(value) {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function AdminNotices() {
  const [notices, setNotices] = useState([])
  const [audienceOptions, setAudienceOptions] = useState(['all', 'GP', 'LP', 'Startup', 'ServiceProvider'])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get('/api/admin/notices')
      setNotices(res.data?.notices || [])
      if (res.data?.audienceOptions?.length) setAudienceOptions(res.data.audienceOptions)
    } catch {
      toast.error('Failed to load notices')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const openCreate = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (notice) => {
    setEditingId(notice.id)
    setForm({
      title: notice.title || '',
      body: notice.body || '',
      audience: notice.audience || 'all',
      isActive: notice.isActive !== false,
    })
    setModalOpen(true)
  }

  const saveNotice = async (e) => {
    e.preventDefault()
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and message are required')
      return
    }
    setSaving(true)
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        audience: form.audience,
        isActive: form.isActive,
      }
      if (editingId) {
        await axios.put(`/api/admin/notices/${editingId}`, payload)
        toast.success('Notice updated')
      } else {
        await axios.post('/api/admin/notices', payload)
        toast.success('Notice published')
      }
      setModalOpen(false)
      await load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (notice) => {
    try {
      await axios.put(`/api/admin/notices/${notice.id}`, {
        isActive: !notice.isActive,
      })
      toast.success(notice.isActive ? 'Notice deactivated' : 'Notice activated')
      await load()
    } catch {
      toast.error('Update failed')
    }
  }

  const removeNotice = async (notice) => {
    if (!window.confirm(`Delete notice "${notice.title}"?`)) return
    try {
      await axios.delete(`/api/admin/notices/${notice.id}`)
      toast.success('Notice deleted')
      await load()
    } catch {
      toast.error('Delete failed')
    }
  }

  return (
    <AdminLayout title="Platform Notices">
      <div className="admin-alert-banner warning" style={{ marginBottom: 16 }}>
        <span>ℹ️</span>
        <div>
          <strong>Login popup notices</strong> are shown to non-admin users after sign-in until they click
          {' '}<em>I&apos;ve Read This / 我已知晓</em>. Users who already acknowledged a notice will not see it again.
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <span className="admin-card-title">Active &amp; Past Notices</span>
          <button type="button" className="admin-btn sm primary" onClick={openCreate}>
            <IconLabel name="bell" size="xs">New Notice</IconLabel>
          </button>
        </div>
        <div className="admin-card-body">
          {loading ? (
            <p style={{ color: '#909399' }}>Loading notices…</p>
          ) : notices.length === 0 ? (
            <p style={{ color: '#909399' }}>No notices yet. Create one to broadcast to all users.</p>
          ) : (
            <div className="admin-table-card embedded">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Audience</th>
                    <th>Status</th>
                    <th>Acknowledged</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {notices.map((notice) => (
                    <tr key={notice.id}>
                      <td>
                        <strong>{notice.title}</strong>
                        <div style={{ fontSize: 12, color: '#909399', marginTop: 4, maxWidth: 360 }}>
                          {(notice.body || '').slice(0, 120)}
                          {(notice.body || '').length > 120 ? '…' : ''}
                        </div>
                      </td>
                      <td>{AUDIENCE_LABELS[notice.audience] || notice.audience}</td>
                      <td>
                        <span className={`admin-status-badge ${notice.isActive ? 'resolved' : 'pending'}`}>
                          {notice.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{notice.acknowledgedCount ?? 0}</td>
                      <td>{formatWhen(notice.createdAt)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="admin-btn sm secondary" onClick={() => openEdit(notice)}>
                            Edit
                          </button>
                          <button type="button" className="admin-btn sm secondary" onClick={() => toggleActive(notice)}>
                            {notice.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button type="button" className="admin-btn sm danger" onClick={() => removeNotice(notice)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <AdminModal
        open={modalOpen}
        title={editingId ? 'Edit Notice' : 'New Notice'}
        subtitle="Users see this as a popup after login until they acknowledge it."
        onClose={() => !saving && setModalOpen(false)}
        footer={(
          <>
            <button type="button" className="admin-btn secondary" disabled={saving} onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" form="admin-notice-form" className="admin-btn primary" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Publish Notice'}
            </button>
          </>
        )}
      >
        <form id="admin-notice-form" onSubmit={saveNotice} className="admin-settings-grid-2">
          <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
            <span className="admin-field-label">Title</span>
            <input
              className="admin-field-input"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Scheduled maintenance this weekend"
              required
            />
          </label>
          <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
            <span className="admin-field-label">Message</span>
            <textarea
              className="admin-field-input"
              rows={6}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Write the notice content users will see in the popup…"
              required
            />
          </label>
          <label className="admin-field">
            <span className="admin-field-label">Audience</span>
            <select
              className="admin-field-input"
              value={form.audience}
              onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
            >
              {audienceOptions.map((key) => (
                <option key={key} value={key}>{AUDIENCE_LABELS[key] || key}</option>
              ))}
            </select>
          </label>
          <label className="admin-field" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 28 }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            <span className="admin-field-label" style={{ margin: 0 }}>Active (show to users)</span>
          </label>
        </form>
      </AdminModal>
    </AdminLayout>
  )
}
